import {
  buildAxiosAuthenticationHeaders,
  buildAxiosAuthenticationHeadersForMultipart,
} from "@src/cloud-connect/api/util/build-axios-authentication-headers";
import { parseMaybeJson } from "@src/cloud-connect/api/util/parse-maybe-json";
import { parseAxiosApiError } from "@src/cloud-connect/utils/parse-axios-api-error";
import { createLinkedAbortController } from "@src/cloud-connect/api/util/create-linked-abort-controller";
import type { StreamRequestOptions } from "@src/cloud-connect/api/util/create-linked-abort-controller";
import { parseSupportTicketSseStreamMessage } from "@src/cloud-connect/api/util/parse-support-ticket-sse-stream";
import { supportUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import {
  CustomerTicket,
  supportTicketByIdType,
  TicketCommentAttachment,
} from "@src/types/support";

export type TicketStreamEvent =
  | { type: "ticket"; ticket: supportTicketByIdType }
  | { type: "files"; attachments: TicketCommentAttachment[] }
  | { type: "token"; text: string }
  | { type: "tool-call"; data: unknown }
  | { type: "done" }
  | { type: "error"; message: string };

const UNEXPECTED_STREAM_END =
  "The connection closed before the response completed.";

function mapTicketSseMessage(message: {
  event: string;
  data: string;
}): TicketStreamEvent | null {
  switch (message.event) {
    case "ticket":
      return {
        type: "ticket",
        ticket: JSON.parse(message.data) as supportTicketByIdType,
      };
    case "files": {
      const parsed = parseMaybeJson(message.data) as {
        attachments?: TicketCommentAttachment[];
      };
      return { type: "files", attachments: parsed?.attachments ?? [] };
    }
    case "token":
      return { type: "token", text: message.data };
    case "tool-call":
    case "tool_call":
      return { type: "tool-call", data: parseMaybeJson(message.data) };
    case "done":
      return { type: "done" };
    case "error":
      return { type: "error", message: message.data };
    case "meta":
    case "heartbeat":
      return null;
    default:
      return null;
  }
}

/**
 * Open a streaming POST to the v2 AI ticket endpoint using the Fetch API.
 * Parses SSE events as they arrive and yields typed events.
 */
export async function* postSupportTicketWithAIStream(
  namespace: string,
  payload: CustomerTicket,
  options?: StreamRequestOptions,
  files?: File[],
): AsyncGenerator<TicketStreamEvent, void, void> {
  const url = supportUrlEnvVar();
  const abortController = createLinkedAbortController(options?.signal);

  const imageFiles = files ?? [];
  const hasFiles = imageFiles.length > 0;
  const endpoint = hasFiles ? "tickets/ai-with-images" : "tickets/ai";
  const fullUrl = `${url}/api/v2/ns/${namespace}/${endpoint}`;

  // Set once a "done" or "error" SSE event is actually seen
  let isAPIResponseCompleted = false;

  try {
    let body: string | FormData;
    let requestHeaders: Record<string, string>;
    if (hasFiles) {
      // FormData bodies must set their own multipart Content-Type — never send
      // a JSON content-type with them.
      requestHeaders = {
        Accept: "text/event-stream",
        ...buildAxiosAuthenticationHeadersForMultipart(),
      };
      const formData = new FormData();
      formData.append("ticket", JSON.stringify(payload));
      for (const file of imageFiles) {
        formData.append("files", file);
      }
      body = formData;
    } else {
      body = JSON.stringify(payload);
      requestHeaders = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...buildAxiosAuthenticationHeaders(),
      };
    }

    // Use fetch here because browser SSE needs stream-reader access (response.body.getReader()).
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: requestHeaders,
      body,
      signal: abortController.signal,
    });

    if (!response.ok) {
      let responseData: unknown;
      try {
        responseData = await response.json();
      } catch {
        // Keep the fallback message when the response is not valid JSON.
      }
      const { message } = parseAxiosApiError(
        { response: { status: response.status, data: responseData } },
        "Failed to submit technical support request.",
      );
      yield { type: "error", message };
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      yield { type: "error", message: "Failed to read response stream." };
      return;
    }

    for await (const message of parseSupportTicketSseStreamMessage(reader)) {
      const mapped = mapTicketSseMessage(message);
      if (mapped === null) continue;

      yield mapped;

      if (mapped.type === "done" || mapped.type === "error") {
        isAPIResponseCompleted = true;
        break;
      }
    }
  } catch (err) {
    if (options?.signal?.aborted) {
      return;
    }
    console.error("postSupportTicketWithAIStream stream read failed:", err);
  } finally {
    abortController.abort();
  }

  // Reached whenever the stream ends (cleanly or via a caught read error) without a
  // "done"/"error" SSE event — e.g. a proxy timeout, a dropped connection, or a chunked
  // response that terminates abnormally (e.g. NS_BINDING_ABORTED).
  if (!isAPIResponseCompleted && !options?.signal?.aborted) {
    yield { type: "error", message: UNEXPECTED_STREAM_END };
  }
}
